import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, TextField, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, InputLabel, Chip, Collapse
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import api from '../axios';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import Footer from '../components/Footer';

const LIME = '#00F1A7';
const DARK = '#1C1C1E';

function safeToString(val) {
  if (val == null) return '-';
  if (typeof val === 'string' || typeof val === 'number') return val;
  if (Array.isArray(val)) return '[array]';
  if (typeof val === 'object' && val.$$typeof) return '[elemento React]';
  if (typeof val === 'object' && val.nome) return val.nome;
  if (typeof val === 'object' && val.data) return val.data;
  try { return JSON.stringify(val); } catch { return '[objeto]'; }
}

function DocumentosAlunoList({ alunoId }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get(`/alunos/${alunoId}/documentos`)
      .then(res => setDocs(res.data))
      .catch(() => setDocs([]));
  }, [alunoId]);

  const handleDeleteDoc = async (docId) => {
    await api.delete(`/documentos/${docId}`);
    api.get(`/alunos/${alunoId}/documentos`)
      .then(res => setDocs(res.data))
      .catch(() => setDocs([]));
  };

  if (!docs.length) return <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>Nenhum documento enviado.</Typography>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {docs.map(doc => (
        <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9', '&:hover': { backgroundColor: '#f0f0f0' } }}>
          <a href={doc.filepath} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#555', fontSize: '0.8rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.filename}
          </a>
          <IconButton size="small" color="error" sx={{ ml: 1, padding: '2px' }} onClick={() => handleDeleteDoc(doc.id)}>
            <CloseIcon fontSize="small" sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

function AlunoTableRow({ aluno, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '&:last-child td': { border: 0 } }}>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{aluno.nome}</Typography></TableCell>
        <TableCell><Typography variant="body2">{aluno.email || '-'}</Typography></TableCell>
        <TableCell><Typography variant="body2">{aluno.telefone || '-'}</Typography></TableCell>
        <TableCell><Typography variant="body2">{aluno.estado_civil || '-'}</Typography></TableCell>
        <TableCell>
          <Chip label={aluno.sexo === 'masculino' ? 'M' : aluno.sexo === 'feminino' ? 'F' : '-'} color={aluno.sexo === 'masculino' ? 'primary' : aluno.sexo === 'feminino' ? 'secondary' : 'default'} size="small" />
        </TableCell>
        <TableCell><Typography variant="body2">{aluno.bairro || '-'}</Typography></TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="Editar">
              <IconButton size="small" color="info" onClick={() => onEdit(aluno.id)}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" color="error" onClick={() => onDelete(aluno.id)}><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 600 }}>Detalhes Completos</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mt: 2 }}>
                <Box><Typography variant="caption" color="text.secondary">Endereço</Typography><Typography variant="body2">{aluno.endereco || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">CEP</Typography><Typography variant="body2">{aluno.cep || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Motivo</Typography><Typography variant="body2">{aluno.motivo || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Tipo de Batismo</Typography><Typography variant="body2">{aluno.tipo_batismo || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Data de Nascimento</Typography><Typography variant="body2">{aluno.data_nascimento || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Observações</Typography><Typography variant="body2">{aluno.observacoes || '-'}</Typography></Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Documentos:</Typography>
                <DocumentosAlunoList alunoId={aluno.id} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Alunos() {
  const navigate = useNavigate();
  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [estadoCivil, setEstadoCivil] = useState('');
  const [motivo, setMotivo] = useState('');
  const [tipoBatismo, setTipoBatismo] = useState('');
  const [endereco, setEndereco] = useState('');
  const [bairro, setBairro] = useState('');
  const [cep, setCep] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [sexo, setSexo] = useState('');
  const [dataNascimento, setDataNascimento] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [arquivosParaUpload, setArquivosParaUpload] = useState([]);

  const carregar = async () => {
    setLoading(true); setErro('');
    try {
      const res = await api.get('/alunos/');
      setAlunos(res.data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dados');
    }
    setLoading(false);
  };
  useEffect(() => { carregar(); }, []);

  const resetForm = () => {
    setEditId(null); setNome(''); setEmail(''); setTelefone(''); setEstadoCivil('');
    setMotivo(''); setTipoBatismo(''); setEndereco(''); setBairro(''); setCep('');
    setObservacoes(''); setSexo(''); setDataNascimento(null); setDocumentos([]);
    setArquivosParaUpload([]); setErro(''); setSucesso(''); setOpenModal(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setErro(''); setSucesso('');
    if (!nome) { setErro('Preencha o nome'); return; }
    try {
      const payload = {
        nome, email, telefone, estado_civil: estadoCivil, motivo,
        tipo_batismo: tipoBatismo, endereco, bairro, cep, observacoes, sexo,
        data_nascimento: dataNascimento ? new Date(dataNascimento).toISOString().slice(0, 10) : null
      };
      if (editId) {
        await api.put(`/alunos/${editId}`, payload);
        if (arquivosParaUpload && arquivosParaUpload.length > 0) {
          const formData = new FormData();
          arquivosParaUpload.forEach(file => formData.append('files', file));
          await api.post(`/alunos/${editId}/documentos`, formData);
        }
        resetForm();
        setSucesso('Aluno atualizado!');
        carregar();
      } else {
        const res = await api.post('/alunos/', payload);
        if (arquivosParaUpload && arquivosParaUpload.length > 0) {
          const formData = new FormData();
          arquivosParaUpload.forEach(file => formData.append('files', file));
          await api.post(`/alunos/${res.data.id}/documentos`, formData);
        }
        resetForm();
        setSucesso('Aluno cadastrado!');
        carregar();
      }
    } catch {
      setErro('Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    setErro('');
    try {
      await api.delete(`/alunos/${id}`);
      setSucesso('Aluno excluído!');
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const handleEdit = async (id) => {
    const aluno = alunos.find(a => a.id === id);
    setEditId(id); setNome(aluno.nome || ''); setEmail(aluno.email || '');
    setTelefone(aluno.telefone || ''); setEstadoCivil(aluno.estado_civil || '');
    setMotivo(aluno.motivo || ''); setTipoBatismo(aluno.tipo_batismo || '');
    setEndereco(aluno.endereco || ''); setBairro(aluno.bairro || '');
    setCep(aluno.cep || ''); setObservacoes(aluno.observacoes || '');
    setSexo(aluno.sexo || ''); setDataNascimento(aluno.data_nascimento || null);
    try {
      const res = await api.get(`/alunos/${id}/documentos`);
      setDocumentos(res.data);
    } catch { setDocumentos([]); }
    setOpenModal(true);
  };

  if (loading) return <><Header onLogout={handleLogout} /><ContextNavBar context="startfy" /><Container sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container><Footer /></>;
  if (erro && !openModal) return <><Header onLogout={handleLogout} /><ContextNavBar context="startfy" /><Container sx={{ py: 4 }}><Alert severity="error">{erro}</Alert></Container><Footer /></>;

  return (
    <>
      <Header onLogout={handleLogout} />
      <ContextNavBar context="startfy" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Alunos</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpenModal(true); }}
            sx={{ bgcolor: LIME, color: DARK, '&:hover': { filter: 'brightness(0.9)' } }}>
            Novo
          </Button>
        </Box>

        {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }}><>{erro}</></Alert>}

        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 40 }} />
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Estado Civil</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Sexo</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Bairro</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alunos.map((aluno) => (
                <AlunoTableRow key={aluno.id} aluno={aluno} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
              {alunos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum aluno cadastrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog Cadastrar/Editar Aluno */}
        <Dialog open={openModal} onClose={resetForm} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{editId ? 'Editar Aluno' : 'Cadastrar Aluno'}</DialogTitle>
          <DialogContent dividers>
            <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField label="Nome" value={nome} onChange={e => setNome(e.target.value)} fullWidth size="small" />
              <TextField label="E-mail" value={email} onChange={e => setEmail(e.target.value)} fullWidth size="small" />
              <TextField label="Telefone" value={telefone} onChange={e => setTelefone(e.target.value)} fullWidth size="small" />
              <TextField select label="Estado Civil" value={estadoCivil} onChange={e => setEstadoCivil(e.target.value)} fullWidth size="small">
                <MenuItem value="">Selecione</MenuItem>
                <MenuItem value="Solteiro">Solteiro</MenuItem>
                <MenuItem value="Casado">Casado</MenuItem>
                <MenuItem value="Viúvo">Viúvo</MenuItem>
                <MenuItem value="Divorciado">Divorciado</MenuItem>
                <MenuItem value="Separado">Separado</MenuItem>
                <MenuItem value="União Estável">União Estável</MenuItem>
                <MenuItem value="Morando Junto">Morando Junto</MenuItem>
              </TextField>
              <TextField select label="Vindo à Lagoinha por" value={motivo} onChange={e => setMotivo(e.target.value)} fullWidth size="small">
                <MenuItem value="">Selecione</MenuItem>
                <MenuItem value="Transferência">Transferência</MenuItem>
                <MenuItem value="Batismo">Batismo</MenuItem>
              </TextField>
              <TextField select label="Tipo de Batismo" value={tipoBatismo} onChange={e => setTipoBatismo(e.target.value)} fullWidth size="small">
                <MenuItem value="">Selecione</MenuItem>
                <MenuItem value="imersão">Imersão</MenuItem>
                <MenuItem value="aspersão">Aspersão</MenuItem>
                <MenuItem value="católico">Católico</MenuItem>
                <MenuItem value="não sou batizado">Não sou batizado</MenuItem>
              </TextField>
              <TextField label="Endereço Completo" value={endereco} onChange={e => setEndereco(e.target.value)} fullWidth size="small" />
              <TextField label="Bairro" value={bairro} onChange={e => setBairro(e.target.value)} fullWidth size="small" />
              <TextField label="CEP" value={cep} onChange={e => setCep(e.target.value)} fullWidth size="small" />
              <TextField select label="Sexo" value={sexo} onChange={e => setSexo(e.target.value)} fullWidth size="small">
                <MenuItem value="">Selecione</MenuItem>
                <MenuItem value="masculino">Masculino</MenuItem>
                <MenuItem value="feminino">Feminino</MenuItem>
              </TextField>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker label="Data de nascimento" value={dataNascimento} onChange={setDataNascimento} inputFormat="dd/MM/yyyy"
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />} />
              </LocalizationProvider>
              <TextField label="Observações" value={observacoes} onChange={e => setObservacoes(e.target.value)} fullWidth size="small" multiline rows={2} />
              <Box>
                <InputLabel>Documentos (máx. 3 arquivos)</InputLabel>
                <input type="file" name="documentos" multiple accept="application/pdf,image/*" onChange={e => { const files = Array.from(e.target.files).slice(0, 3); setArquivosParaUpload(files); }} style={{ marginTop: 8 }} />
                {arquivosParaUpload.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {arquivosParaUpload.length} arquivo(s) selecionado(s)
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Documentos enviados:</Typography>
                {editId && documentos.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Nenhum documento enviado.</Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {documentos.map(doc => (
                    <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9', '&:hover': { backgroundColor: '#f0f0f0' } }}>
                      <a href={doc.filepath} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#555', fontSize: '0.875rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.filename}
                      </a>
                      <IconButton size="small" color="error" sx={{ ml: 1, padding: '4px' }} onClick={async () => {
                        await api.delete(`/documentos/${doc.id}`);
                        const res = await api.get(`/alunos/${editId}/documentos`);
                        setDocumentos(res.data);
                      }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm} startIcon={<CloseIcon />}>Cancelar</Button>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} onClick={handleAdd}
              sx={{ bgcolor: LIME, color: DARK, '&:hover': { filter: 'brightness(0.9)' } }}>
              {editId ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
}
